import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Card, CardContent, CircularProgress, Container, Dialog, Stack, Typography, useTheme } from "@mui/material";
import { useRequest } from "ahooks";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useNavigate } from 'react-router-dom';
import { useCategories } from "../../../../hooks/useCategories";
import useOpenEditCategory from "../../../../hooks/ui-states/useOpenEditCategory";
import { categoriesService } from "../../../../services/categoriesServices";
import { showErrorToast, showSuccessToast } from "../../../../utils/toast";
import { categoryFormSchema } from "../../../forms/schemas/category";
import MainAppBar from "../../../global/MainAppBar";
import BottomBar from "../../../ui/BottomBar/BottomBar";
import { FormField } from "../../../ui/FormField/FormField";
import DynamicSvgIcon from "../../../ui/icons/DynamicSvgIcon";
import CategoryGroupSelector from "../components/CategoryGroupSelector";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CategoryIconSelector from "../components/CategoryIconSelector";
import ColorSelector from "../components/ColorSelector";
import CategoryItem from "../components/CategoryItem";
import { PlusIcon } from "../../../ui/icons";
import { ROUTES } from "../../../../constants/routes";

type CategoryFormData = z.infer<typeof categoryFormSchema>;

const ModalEditCategory = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openEditCategory, setOpenEditCategory, categoryId, categoryType] = useOpenEditCategory();
  const { t } = useTranslation('categories');
  const [categoryGroups, setCategoryGroups] = useState<any>([]);
  const [categoryIcons, setCategoryIcons] = useState<any>([]);
  const [openCategoryGroupModal, setOpenCategoryGroupModal] = useState<boolean>(false);
  const [openCategoryIconModal, setOpenCategoryIconModal] = useState<boolean>(false);

  const [colors] = useState(() => {
    return ['#1FE0FF', '#F476A7', '#6C3DD3', '#5EBD82', '#FF7C47', '#F9CE46', '#0D79F3', '#F61809', '#757F9F'];
  })

  const { isLoading, getCategoryById, mutate } = useCategories(categoryType as CategoryType);
  const { data: categories } = useCategories();
  const [category, setCategory] = useState(() => getCategoryById(categoryId as string));

  useEffect(() => {
    const categoryGroups = categories?.filter(cat => !['others', 'income'].includes(cat.name.toLowerCase()));
    setCategoryGroups(categoryGroups);
    setCategoryIcons(Array.from(new Set(categoryGroups?.flatMap(catGroup => catGroup.categories.map(c => c.icon)))));
  }, [categories]);

  useEffect(() => {
    setCategory(getCategoryById(categoryId as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      icon: '',
      iconColor: '',
      backgroundColor: '',
      categoryGroupId: '',
    }
  });

  const nameValue = useWatch({ control, name: 'name' });
  useEffect(() => {
    if (category && nameValue && nameValue !== category.name) {
      setCategory(prev => ({ ...prev, name: nameValue }) as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue]);

  const { loading: loadingUpdateCategoryGroup, run: doUpdateCategoryGroup } = useRequest(categoriesService.updateCategoryGroup, {
    manual: true,
    onSuccess: () => {
      showSuccessToast(t('form.update_success', { ns: 'categories' }));
      mutate();
      handleClose();
    },
    onError: (error) => {
      showErrorToast(error.message || t('form.update_error', { ns: 'categories' }));
    },
  });

  const { loading, run: doUpdateCategory } = useRequest(categoriesService.updateCategory, {
    manual: true,
    onSuccess: () => {
      showSuccessToast(t('form.update_success', { ns: 'categories' }));
      mutate();
      handleClose();
    },
    onError: (error) => {
      showErrorToast(error.message || t('form.update_error', { ns: 'categories' }));
    },
  });

  const handleClose = () => {
    setOpenEditCategory(false, null, null);
    reset();
  };

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        icon: category.icon,
        iconColor: category.iconColor,
        backgroundColor: category.backgroundColor,
        categoryGroupId: category.parent?.id,
        parent: category.parent
      } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const onSubmit = (data: CategoryFormData) => {
    if (!categoryId || !category) return;

    if (category.isRoot) {
      doUpdateCategoryGroup(categoryId as string, data);
    } else {
      doUpdateCategory(categoryId as string, data);
    }
  };

  const onError = (error: any) => {
    showErrorToast(error.message || t('form.update_error', { ns: 'categories' }));
  }

  const getTitle = () => {
    if (categoryType === 'expense') {
      return t(category?.isRoot ? 'edit_expense_category_group_page_title' : 'edit_expense_category_page_title', { ns: 'categories' });
    }

    return t('edit_income_category_page_title', { ns: 'categories' });
  }

  const renderCategoryTypeExpense = () => {
    return (
      <Stack direction="column" spacing={4} width="100%">
        <FormField
          {...register('name')}
          fullWidth
          type="text"
          variant="standard"
          label={t('fields.name', { ns: 'categories' })}
          error={!!errors.name}
          helperText={errors.name?.message}
          // onChange={event => setCategory({...category, name: event.target.value} as any)}
          slotProps={{
            input: {
              sx: {
                textAlign: 'right',
              },
            }
          }}
        />

        {!category?.isRoot &&
          <Controller
            name="categoryGroupId"
            control={control}
            render={({ field }) => (
              <FormField
                {...field}
                value={category?.parent?.name || ''}
                onClick={() => setOpenCategoryGroupModal(true)}
                label={t('fields.belong_to_group', { ns: 'categories' })}
                variant="standard"
                fullWidth
                slotProps={{
                  input: { sx: { textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' } },
                  htmlInput: {
                    sx: {
                      textAlign: 'right',
                      width: category?.parent?.name ? `${8 * category?.parent?.name.length}px !important` : 'auto',
                      minWidth: 'unset',
                      maxWidth: '135px'
                    }
                  }
                }}
                endAdornment2={(
                  <Box display='inline' sx={{ textAlign: 'right' }}>
                    <DynamicSvgIcon
                      size={30}
                      iconSize={20}
                      svgUrl={category?.parent?.icon || ''}
                      backgroundColor={category?.parent?.iconColor}
                    />
                  </Box>
                )}
              />
            )}
          />}
      </Stack>
    )
  }

  const renderCategoryTypeIncome = () => {
    return (
      <Stack direction="column" spacing={4} width="100%" textAlign="right">
        <FormField
          {...register('name')}
          fullWidth
          type="text"
          variant="standard"
          label={t('fields.name', { ns: 'categories' })}
          error={!!errors.name}
          helperText={errors.name?.message}
          // onChange={event => setCategory({...category, name: event.target.value} as any)}
          slotProps={{
            input: {
              sx: {
                textAlign: 'right',
              },
            }
          }}
        />
      </Stack>
    )
  }

  const renderForm = () => {
    if (categoryType === 'expense') {
      return renderCategoryTypeExpense();
    }

    return renderCategoryTypeIncome();
  }

  if (!categoryId || !categoryType) {
    return <></>;
  }

  const handleOnChangeColor = (color: string) => {
    const newCategory = { ...category };
    if (category?.isRoot) {
      // Update category group color;
      newCategory.iconColor = color;

      // Update sub-categories color;
      newCategory.categories = (newCategory.categories || []).map(cat => ({ ...cat, iconColor: '#fff', backgroundColor: color }));
    } else if (categoryType === 'income') {
      // Update income category
      newCategory.iconColor = '#fff';
      newCategory.backgroundColor = color;
    }

    setCategory(newCategory as any);
  }

  return (
    <>


      <Dialog
        fullScreen
        scroll="body"
        open={openEditCategory}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            overflow: 'hidden'
          },
        }}
      >
        <Box sx={{ height: '100%' }}>
          <MainAppBar
            preventGoBack
            elevation={1}
            beforeGoBack={handleClose}
            title={getTitle()}
            sx={{
              // boxShadow: theme.shadows[1],
              // backgroundColor: theme.palette.background.default,
              backgroundColor: theme.palette.common.white,
              overflow: 'hidden'
            }}
          />
          {isLoading ? (
            <Stack justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Stack>
          ) : (
            category && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Container>
                  <Stack justifyContent="center" alignItems="center" spacing={4}>
                    <Box display='flex' alignItems='center' justifyContent='center' gap={1}>
                      <DynamicSvgIcon
                        size={60}
                        iconSize={36}
                        svgUrl={watch('icon') || category.icon}
                        color={watch('iconColor') || category.iconColor}
                        backgroundColor={watch('backgroundColor') || category.backgroundColor}
                      />
                      <Card elevation={4} sx={{ width: '24px', height: '24px', borderRadius: '50%' }}>
                        <KeyboardArrowRightIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenCategoryIconModal(true);
                            // if (datePicker) {
                            //   setDatePicker(datePicker.add(1, "day"));
                            // }
                          }}
                        />
                      </Card>
                    </Box>

                    {(category.isRoot || categoryType === 'income') && (
                      <ColorSelector color={category.iconColor} colors={colors} onSelect={handleOnChangeColor} />
                    )}

                    <Card elevation={4} sx={{ borderRadius: 3, width: '100%' }}>
                      <CardContent sx={{ py: 4, px: 3 }}>
                        {renderForm()}
                      </CardContent>
                    </Card>

                    {category.isRoot && (
                      <Card elevation={4} sx={{ borderRadius: 3, width: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography color="#A2A9BE" fontSize="14px">{t('list_categories', { ns: 'categories' })}</Typography>
                            <PlusIcon color="#475480" fontSize="30px" onClick={() => navigate({ pathname: ROUTES.CATEGORIES, search: `?categoryGroupId=${category.id}` })} />
                          </Box>
                          <Box marginTop="20px">
                            {(category.categories || []).map(cat => (
                              <CategoryItem category={cat} divider onEdit={() => setOpenEditCategory(true, cat.id, categoryType)} />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                  </Stack>
                </Container>
                <BottomBar>
                  <Container>
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={loading || loadingUpdateCategoryGroup}
                      onClick={handleSubmit(onSubmit, onError)}
                    >
                      {loading || loadingUpdateCategoryGroup ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t('save', { ns: 'common' })
                      )}
                    </Button>
                  </Container>
                </BottomBar>
              </form>
            )
          )}
        </Box>

        <CategoryGroupSelector
          // currentValue={category?.parent?.id}
          categoryGroups={categoryGroups}
          open={openCategoryGroupModal}
          onSelect={(catGroup) => setCategory({ ...category, parent: catGroup } as any)}
          onClose={() => setOpenCategoryGroupModal(false)}
        />

        <CategoryIconSelector
          categoryIcons={categoryIcons}
          open={openCategoryIconModal}
          onSelect={(icon) => setCategory({ ...category, icon: icon } as any)}
          onClose={() => setOpenCategoryIconModal(false)}
        />
      </Dialog>
    </>
  );
};

export default ModalEditCategory;